import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Building2, Plus, Users, Briefcase, LogOut, ShieldCheck, KeyRound,
  Trash2, Loader2, Copy, Check, Power, PowerOff, CreditCard, Pencil,
} from "lucide-react";

const STATUS_META = {
  trial: { label: "Prova", cls: "bg-blue-100 text-blue-800 hover:bg-blue-100" },
  active: { label: "Attivo", cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" },
  suspended: { label: "Sospeso", cls: "bg-gray-200 text-gray-700 hover:bg-gray-200" },
  expired: { label: "Scaduto", cls: "bg-red-100 text-red-800 hover:bg-red-100" },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; }
};

function CreateOrgDialog({ plans, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "", plan_id: "starter" });
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => { setForm({ name: "", admin_name: "", admin_email: "", admin_password: "", plan_id: "starter" }); setCreated(null); setCopied(false); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/platform/orgs", form);
      setCreated({ email: form.admin_email, password: form.admin_password });
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore creazione studio");
    } finally {
      setLoading(false);
    }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(`Email: ${created.email}\nPassword: ${created.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="new-org-btn" className="rounded-none font-medium">
          <Plus className="w-4 h-4 mr-2" /> Nuovo studio
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-md">
        {!created ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading font-black tracking-tight">Crea nuovo studio</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label className="data-label">Nome studio</Label>
                <Input data-testid="org-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 rounded-none" placeholder="Studio Rossi Consulting" />
              </div>
              <div>
                <Label className="data-label">Nome admin</Label>
                <Input data-testid="org-admin-name-input" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} required className="mt-1 rounded-none" placeholder="Mario Rossi" />
              </div>
              <div>
                <Label className="data-label">Email admin</Label>
                <Input type="email" data-testid="org-admin-email-input" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required className="mt-1 rounded-none" placeholder="admin@studiorossi.it" />
              </div>
              <div>
                <Label className="data-label">Password admin (min. 8 caratteri)</Label>
                <Input type="text" data-testid="org-admin-password-input" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} required minLength={8} className="mt-1 rounded-none font-mono" placeholder="Password iniziale" />
              </div>
              <div>
                <Label className="data-label">Piano di abbonamento</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="org-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} — € {p.price_monthly}/mese</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Lo studio parte in prova gratuita di {plans.find((p) => p.id === form.plan_id)?.trial_days ?? 14} giorni.</p>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="create-org-submit-btn" disabled={loading} className="rounded-none w-full font-medium">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crea studio
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading font-black tracking-tight flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-600" /> Studio creato
              </DialogTitle>
            </DialogHeader>
            <div className="mt-2 space-y-3" data-testid="org-created-credentials">
              <p className="text-sm text-muted-foreground">Comunica queste credenziali al consulente. La password non sarà più visibile.</p>
              <div className="border border-border bg-secondary/40 p-4 space-y-2 font-mono text-sm">
                <div><span className="data-label">Email</span><div className="font-medium break-all">{created.email}</div></div>
                <div><span className="data-label">Password</span><div className="font-medium break-all">{created.password}</div></div>
              </div>
              <Button data-testid="copy-creds-btn" variant="outline" onClick={copyCreds} className="rounded-none w-full font-medium">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copiato" : "Copia credenziali"}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => { setOpen(false); reset(); }} className="rounded-none w-full font-medium">Chiudi</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResetAdminDialog({ org, onDone }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/platform/orgs/${org.id}/reset-admin`, { password });
      toast.success("Password admin aggiornata");
      setOpen(false); setPassword(""); onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPassword(""); }}>
      <DialogTrigger asChild>
        <Button data-testid={`reset-admin-btn-${org.id}`} variant="ghost" size="sm" className="rounded-none h-8 px-2 text-muted-foreground hover:text-foreground" title="Reset password admin">
          <KeyRound className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tight">Reset password admin</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <p className="text-sm text-muted-foreground">Studio: <span className="font-medium text-foreground">{org.name}</span></p>
          <div>
            <Label className="data-label">Nuova password (min. 8)</Label>
            <Input type="text" data-testid="reset-admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="mt-1 rounded-none font-mono" />
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="reset-admin-submit-btn" disabled={loading} className="rounded-none w-full font-medium">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Aggiorna password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionDialog({ org, plans, onDone }) {
  const cur = org.subscription || {};
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    plan_id: cur.plan_id || "starter",
    status: cur.status || "trial",
    expires_at: (cur.expires_at || "").slice(0, 10),
    trial_ends_at: (cur.trial_ends_at || "").slice(0, 10),
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/platform/orgs/${org.id}/subscription`, {
        plan_id: form.plan_id,
        status: form.status,
        expires_at: form.expires_at || null,
        trial_ends_at: form.trial_ends_at || null,
      });
      toast.success("Abbonamento aggiornato");
      setOpen(false); onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`subscription-btn-${org.id}`} variant="ghost" size="sm" className="rounded-none h-8 px-2 text-muted-foreground hover:text-foreground" title="Gestisci abbonamento">
          <CreditCard className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tight">Abbonamento — {org.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="data-label">Piano</Label>
            <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
              <SelectTrigger className="mt-1 rounded-none" data-testid="sub-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} — € {p.price_monthly}/mese</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="data-label">Stato</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1 rounded-none" data-testid="sub-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trial">Prova gratuita</SelectItem>
                <SelectItem value="active">Attivo (pagante)</SelectItem>
                <SelectItem value="suspended">Sospeso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.status === "trial" && (
            <div>
              <Label className="data-label">Fine prova (vuoto = auto)</Label>
              <Input type="date" data-testid="sub-trial-ends-input" value={form.trial_ends_at} onChange={(e) => setForm({ ...form, trial_ends_at: e.target.value })} className="mt-1 rounded-none" />
            </div>
          )}
          {form.status === "active" && (
            <div>
              <Label className="data-label">Scadenza abbonamento (vuoto = nessuna)</Label>
              <Input type="date" data-testid="sub-expires-input" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 rounded-none" />
            </div>
          )}
          <DialogFooter>
            <Button type="submit" data-testid="sub-save-btn" disabled={loading} className="rounded-none w-full font-medium">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salva abbonamento
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrgRow({ org, plans, onRefresh }) {
  const status = org.subscription_status ? STATUS_META[org.subscription_status] : null;
  const toggleActive = async () => {
    try {
      await api.put(`/platform/orgs/${org.id}`, { active: !org.active });
      toast.success(org.active ? "Studio disattivato" : "Studio attivato");
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
  };
  const del = async () => {
    try {
      await api.delete(`/platform/orgs/${org.id}`);
      toast.success("Studio eliminato");
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
  };
  return (
    <div data-testid={`org-row-${org.id}`} className="border border-border bg-white p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-primary/40 transition-colors duration-150">
      <div className="w-10 h-10 bg-primary flex items-center justify-center shrink-0">
        <Building2 className="w-5 h-5 text-primary-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-black tracking-tight" data-testid={`org-name-${org.id}`}>{org.name}</span>
          {org.active ? (
            <Badge className="rounded-none bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Attivo</Badge>
          ) : (
            <Badge variant="secondary" className="rounded-none">Disattivato</Badge>
          )}
          {org.plan && (
            <Badge variant="outline" className="rounded-none" data-testid={`org-plan-badge-${org.id}`}>{org.plan.name}</Badge>
          )}
          {status && (
            <Badge className={`rounded-none ${status.cls}`} data-testid={`org-substatus-${org.id}`}>
              <CreditCard className="w-3 h-3 mr-1" />{status.label}
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 break-all">
          {org.admin_email || "—"}
          {org.subscription?.status === "trial" && org.subscription?.trial_ends_at && (
            <span className="ml-2">· prova fino al {fmtDate(org.subscription.trial_ends_at)}</span>
          )}
          {org.subscription?.status === "active" && org.subscription?.expires_at && (
            <span className="ml-2">· scade il {fmtDate(org.subscription.expires_at)}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-5 text-sm shrink-0">
        <div className="flex items-center gap-1.5 text-muted-foreground" title="Utenti">
          <Users className="w-4 h-4" /> {org.users_count ?? 0}{org.plan?.max_users != null && <span className="text-xs">/{org.plan.max_users}</span>}
        </div>
        <div className="flex items-center gap-1.5 text-muted-foreground" title="Clienti">
          <Briefcase className="w-4 h-4" /> {org.clients_count ?? 0}{org.plan?.max_clients != null && <span className="text-xs">/{org.plan.max_clients}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <SubscriptionDialog org={org} plans={plans} onDone={onRefresh} />
        <ResetAdminDialog org={org} onDone={onRefresh} />
        <Button data-testid={`toggle-org-btn-${org.id}`} variant="ghost" size="sm" onClick={toggleActive} className="rounded-none h-8 px-2 text-muted-foreground hover:text-foreground" title={org.active ? "Disattiva" : "Attiva"}>
          {org.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid={`delete-org-btn-${org.id}`} variant="ghost" size="sm" className="rounded-none h-8 px-2 text-muted-foreground hover:text-destructive" title="Elimina">
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading font-black tracking-tight">Eliminare "{org.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                Verranno eliminati definitivamente tutti gli utenti, clienti, assessment e dati dello studio. Azione irreversibile.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">Annulla</AlertDialogCancel>
              <AlertDialogAction data-testid={`confirm-delete-org-btn-${org.id}`} onClick={del} className="rounded-none bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function EditPlanDialog({ plan, onSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: plan.name,
    price_monthly: plan.price_monthly,
    max_users: plan.max_users ?? "",
    unlimited_users: plan.max_users == null,
    max_clients: plan.max_clients ?? "",
    unlimited_clients: plan.max_clients == null,
    trial_days: plan.trial_days ?? 14,
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        price_monthly: parseFloat(form.price_monthly),
        trial_days: parseInt(form.trial_days, 10),
        unlimited_users: form.unlimited_users,
        unlimited_clients: form.unlimited_clients,
      };
      if (!form.unlimited_users) payload.max_users = parseInt(form.max_users, 10);
      if (!form.unlimited_clients) payload.max_clients = parseInt(form.max_clients, 10);
      await api.put(`/platform/plans/${plan.id}`, payload);
      toast.success("Piano aggiornato");
      setOpen(false); onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore aggiornamento piano");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`edit-plan-btn-${plan.id}`} variant="outline" size="sm" className="rounded-none w-full font-medium">
          <Pencil className="w-3.5 h-3.5 mr-2" /> Modifica piano
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tight">Modifica piano {plan.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="data-label">Nome</Label>
            <Input data-testid="plan-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 rounded-none" />
          </div>
          <div>
            <Label className="data-label">Prezzo mensile (€)</Label>
            <Input type="number" min="0" step="0.01" data-testid="plan-price-input" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} required className="mt-1 rounded-none font-mono" />
          </div>
          <div>
            <Label className="data-label">Max utenti</Label>
            <div className="flex items-center gap-3 mt-1">
              <Input type="number" min="1" data-testid="plan-max-users-input" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} disabled={form.unlimited_users} required={!form.unlimited_users} className="rounded-none font-mono" />
              <label className="flex items-center gap-1.5 text-sm whitespace-nowrap cursor-pointer">
                <input type="checkbox" data-testid="plan-unlimited-users-checkbox" checked={form.unlimited_users} onChange={(e) => setForm({ ...form, unlimited_users: e.target.checked })} /> Illimitati
              </label>
            </div>
          </div>
          <div>
            <Label className="data-label">Max clienti</Label>
            <div className="flex items-center gap-3 mt-1">
              <Input type="number" min="1" data-testid="plan-max-clients-input" value={form.max_clients} onChange={(e) => setForm({ ...form, max_clients: e.target.value })} disabled={form.unlimited_clients} required={!form.unlimited_clients} className="rounded-none font-mono" />
              <label className="flex items-center gap-1.5 text-sm whitespace-nowrap cursor-pointer">
                <input type="checkbox" data-testid="plan-unlimited-clients-checkbox" checked={form.unlimited_clients} onChange={(e) => setForm({ ...form, unlimited_clients: e.target.checked })} /> Illimitati
              </label>
            </div>
          </div>
          <div>
            <Label className="data-label">Giorni di prova</Label>
            <Input type="number" min="0" data-testid="plan-trial-days-input" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} required className="mt-1 rounded-none font-mono" />
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="plan-save-btn" disabled={loading} className="rounded-none w-full font-medium">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salva piano
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlansTab({ plans, orgs, onRefresh }) {
  const orgsOnPlan = (pid) => orgs.filter((o) => o.subscription?.plan_id === pid).length;
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">I piani definiscono limiti e prezzo mensile applicati agli studi. Le modifiche hanno effetto immediato su tutti gli studi con quel piano.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-list">
        {plans.map((p) => (
          <div key={p.id} className="border border-border bg-white p-6 flex flex-col" data-testid={`plan-card-${p.id}`}>
            <div className="font-heading font-black text-xl tracking-tight">{p.name}</div>
            <div className="mt-2 font-heading font-black text-3xl tracking-tighter">
              € {p.price_monthly}<span className="text-sm font-medium text-muted-foreground"> /mese</span>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-muted-foreground flex-1">
              <div className="flex items-center gap-2"><Users className="w-4 h-4" /> {p.max_users == null ? "Utenti illimitati" : `${p.max_users} utent${p.max_users === 1 ? "e" : "i"}`}</div>
              <div className="flex items-center gap-2"><Briefcase className="w-4 h-4" /> {p.max_clients == null ? "Clienti illimitati" : `${p.max_clients} clienti`}</div>
              <div className="flex items-center gap-2"><CreditCard className="w-4 h-4" /> Prova gratuita {p.trial_days} giorni</div>
              <div className="text-xs pt-2">{orgsOnPlan(p.id)} studi su questo piano</div>
            </div>
            <div className="mt-4">
              <EditPlanDialog plan={p} onSaved={onRefresh} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PlatformAdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orgs");

  const load = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([api.get("/platform/orgs"), api.get("/platform/plans")]);
      setOrgs(o.data);
      setPlans(p.data);
    } catch {
      setOrgs([]);
      toast.error("Impossibile caricare i dati. Riprova.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const totalUsers = orgs.reduce((a, o) => a + (o.users_count || 0), 0);
  const totalClients = orgs.reduce((a, o) => a + (o.clients_count || 0), 0);
  const mrr = orgs.reduce((a, o) => a + (o.subscription_status === "active" && o.plan ? o.plan.price_monthly : 0), 0);

  return (
    <div className="min-h-screen bg-background" data-testid="platform-admin-page">
      <header className="h-16 border-b border-border bg-primary text-primary-foreground flex items-center justify-between px-4 sm:px-8">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
          <div>
            <div className="font-heading font-black text-sm tracking-tight leading-none">ITASSESS · PLATFORM</div>
            <div className="text-[11px] uppercase tracking-widest text-primary-foreground/60 mt-0.5">Super Admin</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-primary-foreground/80 hidden sm:inline" data-testid="platform-user-email">{user?.email}</span>
          <button data-testid="platform-logout-btn" onClick={handleLogout} className="text-primary-foreground/80 hover:text-white transition-colors" title="Esci"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">Gestione piattaforma</h1>
            <p className="text-sm text-muted-foreground mt-1">Studi, utenti e piani di sottoscrizione mensile.</p>
          </div>
          <CreateOrgDialog plans={plans} onCreated={load} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="border border-border bg-white p-5">
            <div className="data-label">Studi</div>
            <div className="font-heading font-black text-3xl mt-1" data-testid="stat-orgs">{loading ? "—" : orgs.length}</div>
          </div>
          <div className="border border-border bg-white p-5">
            <div className="data-label">Utenti totali</div>
            <div className="font-heading font-black text-3xl mt-1" data-testid="stat-users">{loading ? "—" : totalUsers}</div>
          </div>
          <div className="border border-border bg-white p-5">
            <div className="data-label">Clienti totali</div>
            <div className="font-heading font-black text-3xl mt-1" data-testid="stat-clients">{loading ? "—" : totalClients}</div>
          </div>
          <div className="border border-border bg-white p-5">
            <div className="data-label">MRR attivo</div>
            <div className="font-heading font-black text-3xl mt-1" data-testid="stat-mrr">{loading ? "—" : `€ ${mrr}`}</div>
          </div>
        </div>

        <div className="flex border-b border-border mb-6">
          <button data-testid="tab-orgs-btn" onClick={() => setTab("orgs")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "orgs" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            Studi
          </button>
          <button data-testid="tab-plans-btn" onClick={() => setTab("plans")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "plans" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            Piani di abbonamento
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : tab === "plans" ? (
          <PlansTab plans={plans} orgs={orgs} onRefresh={load} />
        ) : orgs.length === 0 ? (
          <div className="border border-dashed border-border bg-white p-12 text-center" data-testid="no-orgs">
            <Building2 className="w-10 h-10 mx-auto text-muted-foreground/50" />
            <p className="mt-3 text-muted-foreground">Nessuno studio ancora. Crea il primo studio di consulenza.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orgs-list">
            {orgs.map((o) => <OrgRow key={o.id} org={o} plans={plans} onRefresh={load} />)}
          </div>
        )}
      </main>
    </div>
  );
}
